import { Card, Chip, Grid, GridColumn, GridRow, Select } from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { useSetCrumbTitle } from "../../contexts/CrumbTitleContext";
import { UserStatusIcon } from "../../components/brand/UserStatusIcon";
import { StatusBadge } from "../../components/StatusBadge/StatusBadge";
import { AuditActivityList } from "../../components/AuditActivityList/AuditActivityList";
import { AccountCard } from "../../components/Persons/AccountCard/AccountCard";
import { DiagnoseBadges } from "../../components/Persons/DiagnoseBadges/DiagnoseBadges";
import { EditAccountModal } from "../../components/Persons/EditAccountModal/EditAccountModal";
import { ProfileActions } from "../../components/Persons/ProfileActions/ProfileActions";
import { ProductAccessList } from "../../components/Persons/ProductAccessList/ProductAccessList";
import { useAuthClaims } from "../../hooks/useAuthClaims";
import { DetailRow } from "../../components/DetailRow/DetailRow";
import {
  ACCOUNT_STATUS_BADGE,
  ACCOUNT_STATUS_ICON,
  accountStatus,
} from "../../utils/accountStatus";
import { diagnose, diagUserFromAccount } from "../../utils/diagnose";
import { invitationProductUrls, invitationProductsFromClientIds } from "../../utils/product";
import { fmtDate, fmtDateTime } from "../../utils/format";
import styles from "./PersonPage.module.scss";

// Colocated fragment: the full identity/profile detail the page renders.
const PersonPage_User = graphql(`
  fragment PersonPage_User on User {
    uuid
    email
    emailVerified
    newEmail
    impersonateOnly
    invitationOtpValidUntil
    invitationFrontendUrl
    resetPasswordOtpValidUntil
    passwordChangedAt
    recoveryCodesGeneratedAt
    lastLogin
    preferredLanguage
    twoFactorMethods {
      uuid
      name
      type
    }
    federatedClient {
      uuid
      name
      label
      issuer
    }
    profiles {
      uuid
      listName
      firstName
      lastName
      birthday
      email
      groups {
        uuid
        name
      }
      organization {
        uuid
        name
        licensedApplicationsConnection {
          edges {
            properties {
              notBefore
              notAfter
              licensedModules
              tecartContractId
            }
            node {
              uuid
              clientId
              name
            }
          }
        }
      }
      rolesConnection {
        edges {
          properties {
            names
          }
          node {
            __typename
            ... on Organization {
              uuid
            }
          }
        }
      }
      grantedApplications {
        uuid
        name
        clientId
        uri
      }
    }
    profilesConnection {
      totalCount
    }
  }
`);

const PersonPageQuery = graphql(`
  query PersonPage_Query($uuid: ID!) {
    users(where: { uuid: $uuid }) {
      ...PersonPage_User
    }
  }
`);

/**
 * Support view of one account. Header = the account, titled by its login
 * address. Top row = identity, login diagnosis, activity (three columns);
 * bottom row = the *profile* in the selected organization (name, roles/groups,
 * product access). The header actions edit the account, the profile row's
 * menu edits the profile.
 */
export function PersonPage() {
  const { userId, orgId } = useParams();
  const navigate = useNavigate();
  const { pimAuthClaims } = useAuthClaims();
  const [editingAccount, setEditingAccount] = useState(false);
  // The omni/system org is identified via auth claims (no `system` flag in the
  // schema); used below to hide it from the profile switch.
  const omniUuid = useMemo(() => {
    try {
      return pimAuthClaims.getOmniOrganization().uuid;
    } catch {
      return null;
    }
  }, [pimAuthClaims]);
  const [{ data }, refetch] = useQuery({
    query: PersonPageQuery,
    variables: { uuid: userId ?? "" },
    pause: !userId,
  });
  const user = getFragmentData(PersonPage_User, data?.users ?? [])[0];
  // Account/profile mutations change what the header, diagnostics and cards show.
  const reload = useCallback(() => refetch({ requestPolicy: "network-only" }), [refetch]);

  const profiles = user?.profiles ?? [];
  const profileUuids = useMemo(() => profiles.map((p) => p.uuid), [profiles]);
  const activeProfile = profiles.find((p) => p.organization?.uuid === orgId) ?? profiles[0];
  // An account is its login address; the person's name belongs to the profile.
  const title = user?.email || user?.uuid || "—";
  const name = activeProfile?.listName || title;

  useSetCrumbTitle(user ? title : undefined);
  useEffect(() => {
    const previous = document.title;
    document.title = `${user ? title : "Konto"} · Schulsuite-Admin`;
    return () => {
      document.title = previous;
    };
  }, [user, title]);

  if (!user) {
    return <p style={{ color: "var(--color-grey)" }}>Benutzer nicht gefunden.</p>;
  }

  const status = accountStatus(user);
  const badge = ACCOUNT_STATUS_BADGE[status];
  const statusIcon = ACCOUNT_STATUS_ICON[status];
  // The diagnosis lives in the header as badges; the account card shows the
  // banner and the fixes when something fails.
  const diagnosis = diagnose(diagUserFromAccount(user));
  // E-mail and invitation state is what the status badge already says.
  const headerChecks = diagnosis.checks.filter((c) => c.key !== "email" && c.key !== "invite");

  // The profile switch hides the omni/system org (omniUuid is computed above).
  const realProfiles = profiles.filter(
    (p) => p.organization?.uuid && p.organization.uuid !== omniUuid,
  );

  // Roles are directed (RoleTarget = Organization | Group | Profile | User); the
  // person's role *in their org* is only the edges whose node is that organization.
  const orgRoleNames = (p: (typeof profiles)[number]): string[] => {
    const orgUuid = p.organization?.uuid;
    return (p.rolesConnection?.edges ?? [])
      .filter((e) => e.node.__typename === "Organization" && e.node.uuid === orgUuid)
      .flatMap((e) => e.properties.names);
  };
  const activeRoles = activeProfile ? orgRoleNames(activeProfile) : [];
  // "Becker, Anna · Gesamtschule · TEACHER" — the name lives here, not in the header.
  const profileLabel = (p: (typeof profiles)[number]) => {
    const role = orgRoleNames(p)[0];
    return [p.listName, p.organization?.name ?? "—", role].filter(Boolean).join(" · ");
  };
  const profileOptions: SelectOptionType[] = realProfiles.map((p) => ({
    value: p.organization?.uuid ?? "",
    label: profileLabel(p),
  }));
  const activeProfileUuid = activeProfile?.organization?.uuid ?? orgId;

  const org = activeProfile?.organization
    ? { uuid: activeProfile.organization.uuid, name: activeProfile.organization.name }
    : null;
  const licenseEdges = activeProfile?.organization?.licensedApplicationsConnection?.edges ?? [];
  const profileCtx = activeProfile
    ? {
        uuid: activeProfile.uuid,
        name: activeProfile.listName || name,
        grantedAppIds: (activeProfile.grantedApplications ?? []).map((a) => a.uuid),
      }
    : null;
  const groups = activeProfile?.groups ?? [];

  return (
    <div className={styles.stack}>
      {/* header: the account */}
      <Card contentPadding="none">
        <div className={styles.header}>
          <div className={styles.headerMain}>
            <span className={styles.statusIcon} style={{ background: statusIcon.tint }}>
              <UserStatusIcon status={statusIcon.icon} size={28} title={badge.label} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className={styles.titleRow}>
                <h1 className={styles.name}>{title}</h1>
              </div>
              <div className={styles.sub}>
                <StatusBadge status={badge.status} label={badge.label} />
                <span className={styles.subId}>{user.uuid}</span>
              </div>
              <div className={styles.diagnosis}>
                <DiagnoseBadges checks={headerChecks} />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* left: account, then the profile in the selected organization; right: the audit log */}
      <Grid useFormGap>
        <GridRow>
          <GridColumn width={6}>
            <div className={styles.stack}>
              <AccountCard
                user={user}
                profileUuid={activeProfile?.uuid}
                profileCount={user.profilesConnection?.totalCount ?? profiles.length}
                invitationProducts={invitationProductsFromClientIds(
                  (activeProfile?.grantedApplications ?? []).map((a) => a.clientId),
                )}
                invitationProductUrls={invitationProductUrls(
                  activeProfile?.grantedApplications ?? [],
                )}
                onEdit={() => setEditingAccount(true)}
                onChanged={reload}
                onDeleted={() => navigate("/")}
              />
              {/* several profiles: the switch and the count, nothing else */}
              {realProfiles.length > 1 && (
                <div className={styles.profiles}>
                  <div className={styles.profileSelect}>
                    <Select
                      name="profile-switch"
                      options={profileOptions}
                      value={profileOptions.find((o) => o.value === activeProfileUuid) ?? null}
                      isSearchable={realProfiles.length > 8}
                      onChange={(
                        opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>,
                      ) =>
                        navigate(
                          `/users/${user.uuid}/orgs/${String((opt as SelectOptionType).value)}`,
                        )
                      }
                    />
                  </div>
                  <span className={styles.multiHint}>{realProfiles.length} Profile</span>
                </div>
              )}
              <Card>
                <div className={styles.head}>
                  <h2 className={styles.headTitle}>{org ? `Profil · ${org.name}` : "Profil"}</h2>
                  {activeProfile?.organization && (
                    <div className={styles.actions}>
                      <ProfileActions
                        profile={{
                          uuid: activeProfile.uuid,
                          name: activeProfile.listName || name,
                          firstName: activeProfile.firstName,
                          lastName: activeProfile.lastName,
                          email: activeProfile.email,
                          role: activeRoles[0],
                          org: {
                            uuid: activeProfile.organization.uuid,
                            name: activeProfile.organization.name,
                          },
                        }}
                        userUuid={user.uuid}
                        isLastProfile={profiles.length <= 1}
                        onChanged={reload}
                      />
                    </div>
                  )}
                </div>
                {activeProfile ? (
                  <>
                    <DetailRow label="Name">{activeProfile.listName || "—"}</DetailRow>
                    <DetailRow label="Rollen">
                      {activeRoles.length > 0 ? (
                        <div className={styles.roleTags}>
                          {activeRoles.map((r) => (
                            <Chip key={r} value={r} isSmall />
                          ))}
                        </div>
                      ) : (
                        "—"
                      )}
                    </DetailRow>
                    <DetailRow label="Geburtstag">{fmtDate(activeProfile.birthday)}</DetailRow>
                    <DetailRow label="E-Mail (Profil)">{activeProfile.email || "—"}</DetailRow>
                    <DetailRow label="Gruppen">
                      {groups.length > 0 ? (
                        <div className={styles.roleTags}>
                          {groups.map((g) => (
                            <Chip key={g.uuid} value={g.name} isSmall />
                          ))}
                        </div>
                      ) : (
                        "—"
                      )}
                    </DetailRow>
                  </>
                ) : (
                  <p style={{ color: "var(--color-grey)", margin: 0 }}>
                    Kein Profil in einer Organisation.
                  </p>
                )}
              </Card>
              <ProductAccessList
                org={org}
                edges={licenseEdges}
                profile={profileCtx}
                onChanged={reload}
              />
            </div>
          </GridColumn>
          <GridColumn width={6}>
            <Card
              header={{
                headline: "Audit-Log",
                actions: [
                  {
                    text: "Alles anzeigen",
                    hierarchy: "tertiary",
                    callback: () => navigate(`/audit?user=${user.uuid}`),
                  },
                ],
              }}
            >
              <DetailRow label="Letzter Login">{fmtDateTime(user.lastLogin)}</DetailRow>
              <div style={{ marginTop: "var(--spacing-3)" }}>
                <AuditActivityList
                  userUuid={user.uuid}
                  profileUuids={profileUuids}
                  limit={20}
                  emptyNote="Keine protokollierten Ereignisse im letzten Jahr."
                />
              </div>
            </Card>
          </GridColumn>
        </GridRow>
      </Grid>

      {editingAccount && (
        <EditAccountModal
          account={{
            uuid: user.uuid,
            email: user.email,
            preferredLanguage: user.preferredLanguage,
            impersonateOnly: user.impersonateOnly,
          }}
          onClose={() => setEditingAccount(false)}
          onSaved={() => {
            setEditingAccount(false);
            reload();
          }}
        />
      )}
    </div>
  );
}
